import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";
import { ArrowBackRounded, OpenInNewRounded } from "@mui/icons-material";
import color from "../../../Constants/colors";
import { NewsItem } from "../../../Models/ambassador";
import { useNewsItem } from "../../../Query/useAmbassador";
import MarkdownRenderer from "../../ChatUI/MarkdownRenderer";
import { cardSx } from "../../HomePage/DashboardCard";
import { useDashboardColors } from "../../HomePage/dashboardColors";
import { ErrorState, LoadingState } from "../Common/AmbassadorUi";
import { ambassadorPaths } from "../Common/ambassadorNavigation";
import { formatDate } from "../Common/format";
import { useAmbassadorLabels } from "../Common/useAmbassadorLabels";
import { CATEGORY_COLORS } from "./NewsTeaserList";

export const NewsArticleBody: React.FC<{ item: NewsItem }> = ({ item }) => {
  const { t, i18n } = useTranslation("ambassador");
  const labels = useAmbassadorLabels();
  const { primary } = useDashboardColors();

  return (
    <>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: CATEGORY_COLORS[item.category],
        }}
      >
        {labels.newsCategory(item.category).toUpperCase()}
        {item.published_at &&
          ` · ${formatDate(item.published_at, i18n.language)}`}
      </Typography>
      <Typography
        component="h1"
        sx={{
          fontSize: { xs: 24, md: 30 },
          fontWeight: 800,
          color: primary,
          mt: 1,
        }}
      >
        {item.title}
      </Typography>
      {item.teaser && (
        <Typography sx={{ fontSize: 16, color: color.gray600, mt: 1.5 }}>
          {item.teaser}
        </Typography>
      )}
      {item.content && (
        <Box sx={{ mt: 3, color: color.gray900, "& p": { lineHeight: 1.65 } }}>
          <MarkdownRenderer content={item.content} messageRole="assistant" />
        </Box>
      )}
      {item.link && (
        <Button
          variant="outlined"
          color="secondary"
          endIcon={<OpenInNewRounded />}
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ mt: 3 }}
        >
          {t("news.openLink")}
        </Button>
      )}
    </>
  );
};

/** Reading view of one published news item. */
const NewsArticle: React.FC = () => {
  const { t } = useTranslation("ambassador");
  const navigate = useNavigate();
  const { newsId } = useParams();
  const item = useNewsItem(Number(newsId));

  return (
    <Box sx={{ maxWidth: 820, mx: "auto" }}>
      <Button
        color="secondary"
        startIcon={<ArrowBackRounded />}
        onClick={() => navigate(ambassadorPaths.news)}
        sx={{ mb: 2 }}
      >
        {t("news.back")}
      </Button>
      {item.isLoading ? (
        <LoadingState />
      ) : item.isError || !item.data ? (
        <ErrorState text={t("news.notAvailable")} />
      ) : (
        <Box component="article" sx={{ ...cardSx, p: { xs: 2.5, md: 4 } }}>
          <NewsArticleBody item={item.data} />
        </Box>
      )}
    </Box>
  );
};

export default NewsArticle;
